import { BASE_URL } from '../config';
export { BASE_URL };

let isRefreshing = false;
let pendingRequests = [];

async function refreshAccessToken() {
  // Cookie-based: refresh_token httpOnly cookie is sent automatically.
  const response = await fetch(`${BASE_URL}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    throw new Error('Refresh failed');
  }

  // Server sets the new access_token cookie via Set-Cookie header.
  // Nothing to store in localStorage.
  return true;
}

async function redirectToLogin() {
  // Tell the backend to clear the cookie and blacklist the token
  try {
    await fetch(`${BASE_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch {
    // Best effort — proceed even if the logout call fails
  }
  localStorage.removeItem('user');
  window.location.href = '/login';
}

export async function apiFetch(path, options = {}) {
  const url = path.startsWith('http') ? path : `${BASE_URL}${path}`;

  const isFormData = options.body instanceof FormData;
  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...options.headers,
  };

  const controller = new AbortController();
  const timeoutMs = options.timeout || 60000;
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  let response;
  try {
    response = await fetch(url, { ...options, headers, credentials: 'include', signal: controller.signal });
  } catch (err) {
    if (err.name === 'AbortError') throw new Error('Request timed out. Please check your connection and try again.');
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }

  if (response.status === 401) {
    const body = await response.clone().json().catch(() => ({}));
    // Only refresh on token expiry or signature errors — NOT on "No token provided"
    // (when both cookies are absent, refresh will also fail and trigger a redirect loop)
    const isExpired =
      body?.message === 'Token expired' || body?.error === 'Token expired' ||
      body?.message === 'Invalid token';

    if (isExpired) {
      if (isRefreshing) {
        // Queue request until refresh completes
        return new Promise((resolve, reject) => {
          pendingRequests.push(async () => {
            try {
              resolve(await fetch(url, { ...options, headers, credentials: 'include' }));
            } catch (err) {
              reject(err);
            }
          });
        });
      }

      isRefreshing = true;
      try {
        await refreshAccessToken();
        // Retry all queued requests (cookie is now updated by server)
        pendingRequests.forEach((cb) => cb());
        pendingRequests = [];

        // Retry original request
        response = await fetch(url, { ...options, headers, credentials: 'include' });
      } catch {
        pendingRequests = [];
        redirectToLogin();
        throw new Error('Session expired. Please log in again.');
      } finally {
        isRefreshing = false;
      }
    }
  }

  return response;
}
